import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Save, Cancel, Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MyModal from '../MyModal/MyModal';
import { jobReportingPlanModalStyles } from './JobReportingPlanModal.styles';
import { UPDATE_JOB_REPORTING_PLAN } from '../../graphql/mutations/jobs';
import graphqlClient from '../../utils/graphqlClient';
import {  useReportTypes } from '../../hooks';

const JobReportingPlanModal = ({ 
  open, 
  onClose, 
  job, 
  onSuccess, 
  onError
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    reportRequired: false,
    reports: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { reportTypes } = useReportTypes();

  const isApprovalStatus = job?.job_status === 'ONAYLANDI';

  const reportTypeOptions = reportTypes?.map(reportType => ({
    value: reportType.id,
    label: reportType.report_type_name
  })) || [];

  // Status options for reports
  const statusOptions = [
    { value: 'PENDING', label: t('jobReportingPlan.statuses.pending') },
    { value: 'IN_PROGRESS', label: t('jobReportingPlan.statuses.inProgress') },
    { value: 'COMPLETED', label: t('jobReportingPlan.statuses.completed') },
    { value: 'OVERDUE', label: t('jobReportingPlan.statuses.overdue') },
  ];

  useEffect(() => {
    if (open && job) {
      const postApprovalRequired = Boolean(job.post_approval_report_required);
      const postCompletionRequired = Boolean(job.post_completion_report_required);
      
      setFormData({
        reportRequired: isApprovalStatus ? postApprovalRequired : postCompletionRequired,
        reports: (job.job_reports || []).map(r => ({
          // Eski kayıtlarda olmayabileceği için güvenli şekilde varsayılan ekle
          reportTags: Array.isArray(r.reportTags) ? r.reportTags : [],
          ...r,
        }))
      });
      setError(null);
    }
  }, [open, job, isApprovalStatus]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addReport = () => {
    const newReport = {
      id: Date.now(),
      reportType: '',
      startDate: '',
      endDate: '',
      status: 'PENDING',
      reportTags: [],
    };
    setFormData(prev => ({
      ...prev,
      reports: [...prev.reports, newReport]
    }));
  };

  const removeReport = (index) => {
    setFormData(prev => ({
      ...prev,
      reports: prev.reports.filter((_, i) => i !== index)
    }));
  };

  const updateReport = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      reports: prev.reports.map((report, i) => 
        i === index ? { ...report, [field]: value } : report
      )
    }));
  };

  const validateForm = () => {
    if (formData.reportRequired && formData.reports.length === 0) {
      return t('jobReportingPlan.validation.atLeastOneReport');
    }

    for (let i = 0; i < formData.reports.length; i++) {
      const report = formData.reports[i];
      if (!report.reportType) {
        return t('jobReportingPlan.validation.reportTypeRequired', { index: i + 1 });
      }
      if (!report.startDate) {
        return t('jobReportingPlan.validation.startDateRequired', { index: i + 1 });
      }
      if (!report.endDate) {
        return t('jobReportingPlan.validation.endDateRequired', { index: i + 1 });
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateFields = {
        job_reports: formData.reports
      };

      if (isApprovalStatus) {
        updateFields.post_approval_report_required = formData.reportRequired;
      } else {
        updateFields.post_completion_report_required = formData.reportRequired;
      }

      await graphqlClient.mutate(UPDATE_JOB_REPORTING_PLAN, {
        id: job.id,
        updateFields
      });

      // Best-effort: keep active flows in sync with the new/changed reports.
      // Never blocks the save — failures only log.
      try {
        const { autoAddJobToFlowsStandalone } = await import('../../utils/flowAutoAdd');
        await autoAddJobToFlowsStandalone(job.id);
      } catch (autoAddErr) {
        console.error('Auto-add to flows after reporting plan save failed:', autoAddErr);
      }

      const successMessage = t('jobReportingPlan.successMessage', {
        jobTitle: job.job_title,
        status: isApprovalStatus ? t('jobs.jobStatuses.ONAYLANDI') : t('jobs.jobStatuses.TAMAMLANDI')
      });

      onSuccess?.(successMessage);
      onClose();
    } catch (err) {
      const errorMessage = err.message || t('jobReportingPlan.errorMessage');
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!job) return null;

  return (
    <MyModal
      open={open}
      onClose={onClose}
      title={t('jobReportingPlan.title', { 
        status: isApprovalStatus ? t('jobs.jobStatuses.ONAYLANDI') : t('jobs.jobStatuses.TAMAMLANDI')
      })}
      maxWidth="lg"
      fullWidth
      disableBackdropClose
      disableEscapeKeyDown
    >
      <Box sx={jobReportingPlanModalStyles.container}>
        <Paper elevation={2} sx={jobReportingPlanModalStyles.statusSection}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" color="text.primary">
              {t('jobReportingPlan.jobInfo')}
            </Typography>
            <Chip
              label={job?.job_title || '-'}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        <Divider sx={jobReportingPlanModalStyles.divider} />

        <Box sx={jobReportingPlanModalStyles.section}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={jobReportingPlanModalStyles.sectionTitle}
          >
            {isApprovalStatus 
              ? t('jobReportingPlan.postApprovalReportQuestion')
              : t('jobReportingPlan.postCompletionReportQuestion')
            }
          </Typography>

          <FormControl component="fieldset" sx={jobReportingPlanModalStyles.radioGroup}>
            <RadioGroup
              value={formData.reportRequired ? 'yes' : 'no'}
              onChange={(e) => handleInputChange('reportRequired', e.target.value === 'yes')}
            >
              <FormControlLabel 
                value="yes" 
                control={<Radio />} 
                label={t('common.yes')} 
              />
              <FormControlLabel 
                value="no" 
                control={<Radio />} 
                label={t('common.no')} 
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {formData.reportRequired && (
          <>
            <Divider sx={jobReportingPlanModalStyles.divider} />
            <Box sx={jobReportingPlanModalStyles.section}>
              <Box sx={jobReportingPlanModalStyles.reportsHeader}>
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={jobReportingPlanModalStyles.sectionTitle}
                >
                  {t('jobReportingPlan.reports')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={addReport}
                  size="medium"
                  sx={jobReportingPlanModalStyles.addButton}
                >
                  {t('jobReportingPlan.addReport')}
                </Button>
              </Box>

              {formData.reports.length === 0 ? (
                <Alert severity="info" sx={jobReportingPlanModalStyles.noReportsAlert}>
                  {t('jobReportingPlan.noReportsAdded')}
                </Alert>
              ) : (
                <Box sx={jobReportingPlanModalStyles.reportsContainer}>
                  {formData.reports.map((report, index) => (
                    <Paper key={report.id} elevation={1} sx={jobReportingPlanModalStyles.reportCard}>
                      <Box sx={jobReportingPlanModalStyles.reportHeader}>
                        <Typography variant="subtitle1" sx={jobReportingPlanModalStyles.reportTitle}>
                          {t('jobReportingPlan.report')} #{index + 1}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => removeReport(index)}
                          size="small"
                          sx={jobReportingPlanModalStyles.deleteButton}
                        >
                          {t('common.delete')}
                        </Button>
                      </Box>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ minWidth: '150px' }} fullWidth required>
                            <InputLabel >{t('jobReportingPlan.reportType')}</InputLabel>
                            <Select
                              value={report.reportType}
                              onChange={(e) => updateReport(index, 'reportType', e.target.value)}
                              label={t('jobReportingPlan.reportType')}
                            >
                              {reportTypeOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required>
                            <InputLabel>{t('jobReportingPlan.status')}</InputLabel>
                            <Select
                              value={report.status}
                              onChange={(e) => updateReport(index, 'status', e.target.value)}
                              label={t('jobReportingPlan.status')}
                            >
                              {statusOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth sx={{ minWidth: '200px' }}>
                          <InputLabel>{t('jobReportingPlan.reportTags')}</InputLabel>
                          <Select
                            multiple
                            value={Array.isArray(report.reportTags) ? report.reportTags : []}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateReport(
                                index,
                                'reportTags',
                                Array.isArray(value) ? value : [value]
                              );
                            }}
                            label={t('jobReportingPlan.reportTags')}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected || []).map((tag) => (
                                  <Chip key={tag} label={tag} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {(() => {
                              const selectedType = reportTypes?.find(
                                rt => String(rt.id) === String(report.reportType)
                              );
                              const tagOptions = selectedType?.report_type_tags || [];
                              return tagOptions.map(tag => (
                                <MenuItem key={tag} value={tag}>
                                  {tag}
                                </MenuItem>
                              ));
                            })()}
                          </Select>
                        </FormControl>
                      </Grid>
                        
                        {/*
                          Native HTML <input type="date"> — MUI DatePicker'in
                          format="dd.MM.yyyy" mask davranisi elle yazarken
                          bozuluyordu (kullanici "28022026" yazinca yila
                          gecince gun/ay siliniyordu). Browser'in kendi date
                          input'u mask'i sorunsuz yonetir. Value ISO format
                          "YYYY-MM-DD" — Hasura'ya dogrudan gonderilebilir.
                          Takvim iconu tiklaninca native browser picker acilir.
                        */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="date"
                            fullWidth
                            required
                            label={t('jobReportingPlan.startDate')}
                            value={report.startDate || ''}
                            onChange={(e) => {
                              const v = e.target.value || '';
                              updateReport(index, 'startDate', v);
                              // End date before new start → clear end.
                              if (report.endDate && v && new Date(report.endDate) < new Date(v)) {
                                updateReport(index, 'endDate', '');
                              }
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="date"
                            fullWidth
                            required
                            label={t('jobReportingPlan.endDate')}
                            value={report.endDate || ''}
                            onChange={(e) => updateReport(index, 'endDate', e.target.value || '')}
                            slotProps={{
                              inputLabel: { shrink: true },
                              htmlInput: report.startDate ? { min: report.startDate } : undefined,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={jobReportingPlanModalStyles.actions}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            startIcon={<Cancel />}
            size="large"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            size="large"
          >
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Box>
    </MyModal>
  );
};

export default JobReportingPlanModal;
